import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { ExternalLink } from 'lucide-react';

import { customFieldLinkHost } from '@repo/types';
import { ProductModel } from '@repo/ui';
import { detailLabelSx, font, space } from 'settings/tokens';
import { READING_MEASURE } from 'constants/layout';
import { customFieldHtmlHasText, sanitizeCustomFieldHtml } from 'utils/certificateHtml';

interface BrandCustomFieldsProps {
  /** `certificate.product.custom_fields`, as the payload carries it. */
  fields: unknown;
}

/**
 * VRG-104 — the brand's own `rich` and `link` custom fields, rendered below the product
 * description.
 *
 * ## Why this is a slot and not a canvas
 *
 * The epic this sits under (VERA-272) proposed a drag-and-drop module canvas per SKU.
 * That was superseded deliberately: a certificate is an identity document, and its value
 * comes from reading the same way every time — the Carfax / title-deed property. A brand
 * that can rearrange the whole page can also move the parts a reader is relying on, so
 * what a brand gets is a BOUNDED slot in a FIXED spine: their fields, in their order,
 * under the description, never interleaved with the record's own rows.
 *
 * ## No section caption of its own
 *
 * Each `rich` field carries its own label, exactly as `Description` does, so these read
 * as further fields in the Details group rather than a fourth peer heading. Adding a
 * "From the brand" caption would also imply an attribution boundary that is not real
 * here: the title, images and description in this group are already the brand's words.
 * What Vera attests — the certificate id, the status, the provenance — lives elsewhere
 * on the page and is not what this block sits among.
 *
 * ## `text` fields are NOT here
 *
 * They are rows in `ProductDetailsPanel`, where a short label/value pair belongs. The
 * split is by SHAPE, not by importance: a paragraph right-aligned in a facts column
 * wraps to five lines, and a link needs its destination host beside it.
 */
export const BrandCustomFields: React.FC<BrandCustomFieldsProps> = ({ fields }) => {
  const list = Array.isArray(fields) ? fields : [];

  /*
   * `visible !== false` is belt-and-braces: the API already drops hidden fields from
   * every certificate projection, which is where that guarantee has to live (a filter
   * here would still have put the values on the wire). See
   * `resolveVisibleCustomFields` in certificate-data.aggregate.ts.
   */
  /*
   * ⚠️ The label requirement is PER TYPE, and it is not a style preference.
   *
   * It used to be a flat `label.trim()` for all three, which silently dropped any field
   * whose label was empty -- before the branch below ever ran, so it looked like the
   * field had never been authored.
   *
   * For a `link` the label IS the anchor text: with nothing to click, an empty label
   * renders a bare host and a reader has no idea what the destination is for. So a link
   * still needs one, and losing it is the right failure.
   *
   * For `model` and `rich` the VALUE carries the field -- a 3D panel and a block of
   * prose are both legible with no name over them. Requiring a label there forces a
   * heading onto every field, including the second of a pair that describes ONE subject
   * (a stat block and the model of the thing it describes), where the second heading is
   * exactly what you do not want. Those two are judged on whether they have something to
   * show instead; the branches below already return `null` for an empty model url and for
   * rich html that holds no text.
   */
  const blocks = list.filter(
    (field: any) =>
      field &&
      field.visible !== false &&
      (field.type === 'rich' || field.type === 'link' || field.type === 'model') &&
      (field.type === 'link' ? String(field.label ?? '').trim() : true)
  );

  if (blocks.length === 0) return null;

  /*
   * The field name, shared by the `model` and `rich` branches, which each hand-rolled
   * the same Typography before.
   *
   * ## It renders NOTHING for an empty label, and that is a layout decision
   *
   * A label is optional here. Two fields can describe one thing -- a stat block and the
   * model of the object it describes, say -- and only the FIRST should be named; a
   * second heading over the same subject reads as a second subject. Returning `null`
   * rather than an empty `Typography` matters because the parent is a `Stack spacing={3}`,
   * which spaces CHILDREN: an empty element is still a child, so it would contribute a
   * gap and a stray line box, and the pair would drift apart for no visible reason.
   *
   * ## The style is NOT this component's to choose -- see `detailLabelSx`
   *
   * A field name is the certificate's small-label role, the same role `Certificate ID`
   * and `Product ID` play, so it takes the same treatment from the shared constant and
   * only the margin is local.
   *
   * ⚠️ This was briefly `fontWeight: 600`, on the argument that a FIELD NAME must read
   * as one and sat too quiet at `text.secondary`. That argument is not wrong about the
   * label in isolation -- it is wrong about the column: `Certificate ID` sits a few rows
   * above in the same scroll, so a heavier field name made one role render at two
   * weights depending on which file drew it. Spencer's call (2026-11-02) is that the ID
   * labels are the reference. Do not reintroduce a weight here; if a field name really
   * must be heavier, that is a change to the shared treatment so the IDs move with it.
   */
  const FieldLabel = ({ text }: { text: string }) =>
    text ? (
      <Typography sx={{ ...detailLabelSx, mb: `${space.sm}px` }}>{text}</Typography>
    ) : null;

  return (
    <Stack spacing={3}>
      {blocks.map((field: any, idx: number) => {
        const label = String(field.label).trim();
        const value = String(field.value ?? '');

        if (field.type === 'link') {
          /*
           * The HOST, shown next to the link rather than left to the status bar.
           *
           * A link on a page whose entire claim is "this can be trusted" borrows some of
           * that trust — credibility by adjacency — and the reader cannot see where it
           * goes before tapping it on a phone, where there is no hover target. Printing
           * the destination is the cheapest honest mitigation, and it is also why this
           * is a TEXT link and not a button: a styled button reads as the page's own
           * action, i.e. as something Vera is offering.
           *
           * `customFieldLinkHost` returns null for anything that is not an absolute
           * https URL — the same rule the API enforces on the write. A row that somehow
           * holds one anyway (hand-edited, or written before the gate existed) renders
           * as plain text rather than as an unvetted link.
           */
          const host = customFieldLinkHost(value);
          /*
           * Suppress the host line when the LABEL already is the host.
           *
           * The host is printed because a link on a certificate borrows the page's
           * credibility and a phone has no hover target, so the reader is shown where it
           * goes before tapping (see the note above). A brand that labels the link with
           * its own domain has ALREADY satisfied that — printing it a second line down
           * says nothing new and reads as a rendering fault:
           *
           *     danielallencohen.com
           *     danielallencohen.com
           *
           * So this is not a weakening of the mitigation, it is the mitigation already
           * being met by the label. Anything that is not literally the destination host
           * still gets the line, which is the case that matters: "Care guide" over
           * `example.com` tells you something the label does not.
           *
           * Normalised on both sides — scheme, `www.`, trailing slash, case — because a
           * brand typing its own domain as the label types it the way people write it,
           * not the way `URL.hostname` returns it.
           */
          const bareHost = (text: string) =>
            text
              .trim()
              .toLowerCase()
              .replace(/^https?:\/\//, '')
              .replace(/^www\./, '')
              .replace(/\/+$/, '');
          const labelIsHost = Boolean(host) && bareHost(label) === bareHost(host as string);
          if (!host) {
            return (
              <Typography
                key={`${idx}-${label}`}
                sx={{ fontSize: font.body, lineHeight: 1.5, color: 'text.secondary' }}
              >
                {label}
              </Typography>
            );
          }

          return (
            <Stack key={`${idx}-${label}`} spacing={0.5}>
              <Box
                component='a'
                href={value}
                target='_blank'
                // `noopener` is the security half: without it the opened page holds a
                // live handle to this one and can navigate it away — reverse tabnabbing,
                // where the tab the reader comes back to is no longer the certificate
                // they left. `noreferrer` keeps the certificate's URL, which identifies
                // the item, out of the destination's logs.
                rel='noopener noreferrer'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: `${space.sm}px`,
                  fontSize: font.body,
                  lineHeight: 1.5,
                  color: 'text.primary',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  width: 'fit-content',
                }}
              >
                {label}
                <ExternalLink size={14} />
              </Box>
              {!labelIsHost && (
                <Typography
                  sx={{ fontSize: font.label, lineHeight: 1.5, color: 'text.secondary' }}
                >
                  {host}
                </Typography>
              )}
            </Stack>
          );
        }

        if (field.type === 'model') {
          /*
           * VRG-813 — a second 3D model, lower on the page than the hero.
           *
           * ## Why this is not the hero, and why that is the point
           *
           * `products.model_url` holds exactly ONE model and it is the hero — the object
           * the certificate is ABOUT. A product that also wants to show a related object
           * (what the item grants, what it came with, what it unlocks) had no authoring
           * path for it at all: the hero column is taken, and a `rich` field cannot carry
           * an embed because `sanitizeCustomFieldHtml` allows twenty prose tags and no
           * embedding one. That allow-list is deliberate, so the answer is a field TYPE,
           * not a wider allow-list.
           *
           * ## `panel`, not `bare`
           *
           * `bare` means "the caller owns the surface" and is what the hero uses, where
           * the page's own frame paints the plate. Here there is no such frame — the
           * field sits in the brand block among prose — so the viewer has to draw its own
           * bounded box, which is exactly what `surface='panel'` is documented for: "a
           * model that is one item among several on a record page".
           *
           * ## The viewer's own controls are ON here, unlike the hero
           *
           * The hero is edge-to-edge on a phone, where model-viewer's own AR button is
           * already the right affordance. This one is a panel in a column, so a reader who
           * has spun it has no other way back to the opening pose, and AR has no other
           * entry point. `controls` renders Reset, plus AR only where the device can
           * actually enter it.
           *
           * ## Idle spin is ON — reversed from the original design
           *
           * VRG-813 specified it OFF, and the argument was that a second model further
           * down the page is unattended motion in the reader's periphery while they read
           * the text beside it. That argument lost to a stronger one, found by looking at
           * the assembled page rather than at this component alone: a turning hero above
           * inert panels is the SAME OBJECT behaving two ways depending on which block it
           * landed in, which reads as a bug rather than as restraint.
           *
           * The periphery concern is answered instead by the speed and by
           * `prefers-reduced-motion`, which still suppresses it — see the `autoRotate`
           * note at the call site below for the numbers.
           *
           * ⚠️ This heading used to read "Idle spin is OFF" and survived the change that
           * reversed it, so it spent a while asserting the opposite of the line beneath
           * it. If you are changing this behaviour again, change BOTH.
           *
           * A missing or unloadable URL is NOT silently dropped: `ProductModel` renders
           * its own "could not be loaded" state, because on a provenance surface silence
           * about a promised model is indistinguishable from an item that never had one,
           * and those are different facts about the record.
           */
          if (!value.trim()) return null;

          return (
            <Stack key={`${idx}-${label}`}>
              {/* The same FIELD label treatment `rich` uses, so a model reads as a further
                  field in this group rather than a new section. Omitted entirely when the
                  field has no label -- see `FieldLabel`. */}
              <FieldLabel text={label} />
              <ProductModel
                src={value}
                title={label}
                surface='panel'
                /* A ratio rather than a height: the brand block is a column whose width
                   varies with the viewport, and a fixed height would letterbox the model
                   on a phone and crop it on a desktop. 4/3 is the shallower-than-square
                   choice deliberately — this is a supporting panel, and a 1:1 block here
                   would compete with the hero for the page's vertical budget. */
                aspectRatio='4 / 3'
                /* Spins, like every model on this page. The speed and delay are the
                   component's own defaults and deliberately not restated here: 12deg/s
                   after 1s, slow enough to read as an object you could pick up rather
                   than a carousel asking for attention, and `prefers-reduced-motion`
                   still suppresses it. A drag takes over; the spin resumes a second
                   after the hand comes off.

                   This panel used to pass `autoRotate={false}`, which left the
                   certificate with a turning hero above inert panels -- the same object
                   behaving two ways on one page depending on which block it landed in. */
                controls
                /* The hint stays: this is below the fold on a phone, so a reader arriving
                   at it has not necessarily touched the hero and may not know the panel
                   is draggable at all. */
                showHint
                sx={{ maxWidth: `${READING_MEASURE}px` }}
              />
            </Stack>
          );
        }

        // `rich`. An empty tiptap document serialises to `<p></p>`, so a trim check on
        // the raw string would render a label over nothing.
        if (!customFieldHtmlHasText(value)) return null;

        return (
          <Stack key={`${idx}-${label}`}>
            {/* A FIELD label, subordinate to the Details caption above, not a fourth
                section heading. Omitted entirely when empty -- see `FieldLabel`. */}
            <FieldLabel text={label} />
            <Typography
              component='div'
              sx={{
                fontSize: font.body,
                lineHeight: 1.5,
                // The reading measure, for the same reason the description carries it:
                // this is prose, and the record column is wide enough to push a line
                // past the 45–75 character band. See READING_MEASURE.
                maxWidth: `${READING_MEASURE}px`,
                '& p': { m: 0 },
                '& p + p': { mt: `${space.md}px` },
                '& ul, & ol': { my: `${space.md}px`, pl: 3 },
                /*
                  Headings are kept but CAPPED at body size.

                  The brand's editor offers H1–H3 and the sanitizer keeps them, so their
                  structure survives — but an H1 inside a bounded slot would render
                  larger than the certificate's own title and turn a field into the
                  loudest thing on the page. Weight carries the hierarchy instead of
                  size, which is the same trade the field label above makes.
                */
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontSize: font.body,
                  fontWeight: 600,
                  m: 0,
                  mt: `${space.md}px`,
                  '&:first-of-type': { mt: 0 },
                },
                '& blockquote': {
                  m: 0,
                  pl: 2,
                  borderLeft: '2px solid currentColor',
                  opacity: 0.8,
                },
                '& pre': { whiteSpace: 'pre-wrap', m: 0 },
                // Carried across the `style` ban by the sanitizer's own hook — see
                // `registerHooks` in utils/certificateHtml.
                '& [data-align="center"]': { textAlign: 'center' },
                '& [data-align="right"]': { textAlign: 'right' },
                '& [data-align="justify"]': { textAlign: 'justify' },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(value) }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export default BrandCustomFields;
